export enum UserRole {
  TEACHER = 'teacher',
  STUDENT = 'student',
  ADMIN = 'admin',
}

export interface User {
  id: number;
  username: string;
  display_name: string;
  nickname?: string;
  role: UserRole;
  avatar_url?: string;
  security_question?: string;
}

export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

export interface LoginFormValues {
  username: string;
  password: string;
}

export interface RegisterFormValues {
  username: string;
  password: string;
  display_name: string;
  role: UserRole;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, token: string) => void;
  updateUser: (user: User) => void;
  clearAuth: () => void;
}

export type JobType =
  | 'essay'
  | 'code'
  | 'math'
  | 'reading_chinese'
  | 'poetry'
  | 'english_writing'
  | 'english_reading'
  | 'cloze'
  | 'geometry'
  | 'word_problem'
  | 'physics'
  | 'chemistry'
  | 'biology'
  | 'history'
  | 'geography'
  | 'politics'
  | 'choice'
  | 'fill_blank';

/** 客观题题型 */
export type QuestionType = 'single_choice' | 'multiple_choice' | 'fill_blank';

/** 教师设置的题目（选择题 / 填空题） */
export interface Question {
  id: string;
  type: QuestionType;
  stem: string;
  /** 选择题选项（填空题为空） */
  options?: string[];
  /** 正确答案：单选题为字母字符串；多选题为字母数组；填空题为每空参考答案数组（每空可用 | 分隔多个可接受答案） */
  answer: string | string[];
  score: number;
}

/** 学生作答（key 为题目 id） */
export type StudentAnswers = Record<string, string | string[]>;
export type JobStatus = 'draft' | 'published' | 'closed';

export interface DimensionDef {
  name: string;
  max_score: number;
  weight: number;
}

export interface JobOut {
  id: number;
  title: string;
  description?: string;
  subject?: string;
  type: JobType;
  dimensions: DimensionDef[];
  questions?: Question[];
  max_score: number;
  teacher_id: number;
  status: JobStatus;
  due_date?: string;
  class_id?: number | null;
  created_at: string;
  reference_answer?: string | null;
  show_reference?: boolean;
  // 学生视角：我的提交状态（学习通作业卡片状态徽标）
  my_submission_status?: string | null;
  my_score?: number | null;
  review_status?: string | null;
}

export interface ClassroomStudent {
  id: number;
  display_name: string;
  username: string;
  joined_at: string;
}

export interface ClassroomLatestJob {
  id: number;
  title: string;
  due_date: string | null;
}

export interface ClassroomTeacherInfo {
  id: number;
  display_name: string;
  username: string;
  is_owner: boolean;
}

export interface ClassroomOut {
  id: number;
  name: string;
  description?: string | null;
  invite_code: string;
  invite_link: string;
  teacher_name: string;
  teachers?: ClassroomTeacherInfo[];
  member_count: number;
  status: string; // active / archived
  assignment_count: number;
  latest_job?: ClassroomLatestJob | null;
  students?: ClassroomStudent[];
  announcement_count?: number;
  material_count?: number;
}

export interface ClassAnnouncementItem {
  id: number;
  title: string;
  content: string;
  teacher_name: string;
  created_at: string;
  can_delete: boolean;
}

export interface ClassMaterialItem {
  id: number;
  title: string;
  description?: string | null;
  file_name: string;
  file_size: number;
  mime_type: string;
  download_url: string;
  teacher_name: string;
  created_at: string;
  can_delete: boolean;
}

export interface LeaderboardItem {
  rank: number;
  student_id: number;
  display_name: string;
  username: string;
  avg_score: number | null;
  best_score: number | null;
  submitted: number;
  total_jobs: number;
  completion: number;
  reviewed: number;
}

export interface LeaderboardResponse {
  class_name: string;
  anonymous: boolean;
  items: LeaderboardItem[];
  summary: {
    student_count: number;
    job_count: number;
    class_avg: number | null;
    class_completion: number;
  };
}

export interface MistakeExample {
  submission_id: number;
  job_title: string;
  detail: string;
  suggestion: string;
  deduction: number;
}

export interface MistakeItem {
  knowledge_point: string;
  count: number;
  total_deduction: number;
  examples: MistakeExample[];
}

export interface MistakeNotebook {
  mistakes: MistakeItem[];
  /** 扁平错题集：每道错题一条记录，支持收藏 / 重做 */
  items?: MistakeFlatItem[];
  total: number;
  reviewed_count: number;
}

/** 错题集中的一道错题 */
export interface MistakeFlatItem {
  key: string;
  submission_id: number;
  job_id: number;
  job_title: string;
  job_type: string;
  content?: string | null;
  evidence: Evidence;
  score: number | null;
  max_score: number;
  favorite: boolean;
  note?: string | null;
  date: string;
}

/** 错题重做结果 */
export interface RedoResult {
  score: number;
  max_score: number;
  comment?: string | null;
  evidences: Evidence[];
  confidence: string;
}

/** 错题重做历史记录 */
export interface RedoHistoryItem {
  submission_id: number;
  evidence_id: string;
  content: string;
  score: number;
  max_score: number;
  feedback: Evidence[];
  date: string;
}

export interface PracticeQuestion {
  question: string;
}

export interface GeneratedPractice {
  id: number;
  knowledge_point: string;
  questions: PracticeQuestion[];
}

export interface PracticeFeedbackItem {
  question_index: number;
  question: string;
  user_answer: string;
  score: number;
  max_score: number;
  comment: string;
}

export interface PracticeResult {
  score: number;
  max_score: number;
  feedback: PracticeFeedbackItem[];
}

export interface ScoreTrendPoint {
  job_title: string;
  score: number;
  date: string;
}

export interface MasteryItem {
  dimension: string;
  avg_score: number;
  max_score: number;
  mastery_ratio: number;
}

export interface RecentSubmission {
  submission_id: number;
  job_id: number;
  job_title: string;
  status: string;
  score: number | null;
  max_score: number;
  reviewed: boolean;
  date: string;
}

export interface ComparisonDimension {
  dimension: string;
  early_ratio: number;
  late_ratio: number;
  delta: number;
}

export interface StudentReportComparison {
  available: boolean;
  overall: {
    early_avg: number;
    late_avg: number;
    delta: number;
  } | null;
  dimensions: ComparisonDimension[];
  early_count?: number;
  late_count?: number;
}

export interface DimensionTrendPoint {
  date: string;
  job_title: string;
  ratio: number;
}

export interface DimensionTrend {
  dimension: string;
  points: DimensionTrendPoint[];
  latest: number;
  delta: number;
  sample_count: number;
}

export interface StudentReport {
  total_submitted: number;
  reviewed_count: number;
  avg_score: number;
  best_score: number;
  score_trend: ScoreTrendPoint[];
  mastery: MasteryItem[];
  recent: RecentSubmission[];
  comparison?: StudentReportComparison;
  dimension_trend?: DimensionTrend[];
  ai_advice?: string;
}

export interface MySubmission {
  submission_id: number;
  job_id: number;
  job_title: string;
  teacher_name?: string;
  status: string;
  score: number | null;
  max_score: number;
  reviewed: boolean;
  date: string;
}

export interface SubmissionOut {
  id: number;
  job_id: number;
  student_id: number;
  student_name?: string;
  status: string;
  submitted_at: string;
  content?: string;
  file_url?: string;
  score?: number;
  max_score: number;
  review_status?: string | null;
}

export interface MySubmissionOut {
  id: number;
  job_id: number;
  status: string;
  submitted_at: string;
  content?: string | null;
  file_url?: string | null;
  score?: number | null;
  max_score: number;
  review_status?: string | null;
  review_comment?: string | null;
  reject_reason?: string | null;
}

export interface DimensionScore {
  name: string;
  score: number;
  max_score: number;
  weight: number;
}

export interface Evidence {
  id: string;
  dimension: string;
  description: string;
  deduction: number;
  confidence: 'high' | 'medium' | 'low';
  location?: string;
}

export interface ReviewResult {
  id: number;
  submission_id: number;
  score: number;
  max_score: number;
  dimensions: DimensionScore[];
  evidences: Evidence[];
  confidence: 'high' | 'medium' | 'low';
  comment?: string;
  content?: string;
  status: string;
  created_at: string;
  student_name?: string;
  job_title?: string;
  reject_reason?: string;
  job_type?: string | null;
  questions?: Question[];
  details?: QuestionDetail[];
}

/** 客观题逐题批改明细 */
export interface QuestionDetail {
  id: string;
  type: QuestionType;
  stem: string;
  options: string[];
  student_answer: string | string[] | null;
  correct_answer: string | string[];
  is_correct: boolean;
  earned: number;
  max_score: number;
}


export interface Distribution {
  max: number;
  average: number;
  min: number;
  median: number;
}

export interface TopMistake {
  rank: number;
  name: string;
  count: number;
  percentage: number;
}

export interface HeatmapCell {
  name: string;
  value: number;
}

export interface AnalyticsData {
  top_mistakes: TopMistake[];
  distribution: Distribution;
  heatmap: HeatmapCell[];
}

export interface RecommendExercise {
  id: string;
  title: string;
  topic: string;
  difficulty: string;
}

/** 站内消息 */
export interface NotificationItem {
  id: number;
  user_id: number;
  type: string;
  title: string;
  body?: string | null;
  link?: string | null;
  related_id?: number | null;
  is_read: boolean;
  created_at: string;
}

export interface NotificationList {
  items: NotificationItem[];
  unread_count: number;
  total: number;
}

/** 用户反馈（申诉 / 产品问题） */
export interface FeedbackItem {
  id: number;
  user_id: number;
  target_type: string;
  target_id?: number | null;
  category: string;
  content: string;
  status: string;
  reply?: string | null;
  replied_by?: number | null;
  replied_at?: string | null;
  created_at: string;
  job_title?: string | null;
  teacher_name?: string | null;
  student_name?: string | null;
}

/** AI 助手会话 */
export interface AssistantConversation {
  id: number;
  user_id: number;
  persona: string;
  title: string;
  created_at: string;
  updated_at: string;
}

/** AI 助手消息 */
export interface AssistantMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  persona: string;
  conversation_id: number;
  created_at: string;
}

/** 作业提交情况（班级分析用） */
export interface JobSubmissionStatusItem {
  student_id: number;
  display_name: string;
  username: string;
  submitted: boolean;
  status: string;
  score: number | null;
  max_score: number;
  submitted_at: string | null;
}

export interface JobSubmissionStatus {
  job: {
    id: number;
    title: string;
    created_at: string | null;
    due_date: string | null;
    class_id: number | null;
    class_name: string | null;
  };
  total_students: number;
  submitted_count: number;
  unsubmitted_count: number;
  graded_count: number;
  submission_rate: number;
  avg_score: number | null;
  items: JobSubmissionStatusItem[];
}
